import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DIAGNOSTICS_BUCKET,
  DIAGNOSTICS_KEEP_DAYS,
  diagnosticsContentsText,
  diagnosticsKeptText,
  exportSizeText,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';

/** One package as `diagnostics_package` holds it (20261027004000_diagnostics.sql). */
interface PackageRow {
  readonly id: string;
  readonly project_number: string | null;
  readonly notes: string;
  readonly file_name: string;
  readonly object_path: string;
  readonly size_bytes: number;
  readonly contents: readonly { readonly name: string; readonly bytes: number }[];
  readonly left_out: readonly { readonly name: string; readonly reason: string }[];
  readonly app_version: string;
  readonly channel: string;
  readonly status: 'sending' | 'sent';
  readonly sent_by: string | null;
  readonly created_at: string;
  readonly sent_at: string | null;
}

const COLUMNS =
  'id, project_number, notes, file_name, object_path, size_bytes, contents, left_out, app_version, channel, status, sent_by, created_at, sent_at';

const time = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const message = (e: unknown): string =>
  e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e);

/**
 * Diagnostics (tenant admins; parity 06-058, docs/specs/send-diagnostics.md §6): the packages the organisation's
 * people sent from Teslyn Field's Help, newest first, to download (a signed address valid 5 minutes, made with the
 * admin's own sign-in) or delete. Each is removed for good 30 days after it was sent. The database decides who reads
 * and deletes; this page only lists.
 */
export function DiagnosticsScreen({
  sb,
  tenantId,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
}): ReactElement {
  const [rows, setRows] = useState<PackageRow[] | null>(null);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    void (async () => {
      const r = await sb
        .from('diagnostics_package')
        .select(COLUMNS)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(200);
      if (r.error) {
        setError(`Could not load the diagnostics: ${message(r.error)}`);
        return;
      }
      const list = (r.data ?? []) as PackageRow[];
      setRows(list);
      const ids = [...new Set(list.map((p) => p.sent_by).filter((x): x is string => !!x))];
      if (ids.length === 0) return;
      const people = await sb.from('app_user').select('id, email, display_name').in('id', ids);
      if (!people.error)
        setNames(
          new Map(
            ((people.data ?? []) as { id: string; email: string; display_name: string }[]).map((p) => [
              p.id,
              p.display_name || p.email,
            ]),
          ),
        );
    })();
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  async function download(p: PackageRow): Promise<void> {
    const { data, error: e } = await sb.storage
      .from(DIAGNOSTICS_BUCKET)
      .createSignedUrl(p.object_path, 300, { download: p.file_name });
    if (e || !data) {
      setNotice({ ok: false, text: `Could not download ${p.file_name}: ${e?.message ?? 'no address'}` });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener');
  }

  async function remove(p: PackageRow): Promise<void> {
    setDeleting(p.id);
    setNotice(null);
    try {
      const files = await sb.storage.from(DIAGNOSTICS_BUCKET).remove([p.object_path]);
      if (files.error) throw files.error;
      const r = await sb.from('diagnostics_package').delete().eq('id', p.id);
      if (r.error) throw r.error;
      setNotice({ ok: true, text: `Deleted ${p.file_name}.` });
      setRows((cur) => (cur ?? []).filter((x) => x.id !== p.id));
    } catch (e) {
      setNotice({
        ok: false,
        text: `Could not delete ${p.file_name}: ${message(e)}. Two-step sign-in may be needed; sign in again and retry.`,
      });
    } finally {
      setDeleting(null);
      setConfirming(null);
    }
  }

  const now = new Date();
  return (
    <section className="page">
      <PageHeader
        title="Diagnostics"
        subtitle="What your people sent from Teslyn Field with Help, Send diagnostics. Tenant admins only."
      />
      <p className="body">
        Each package is a ZIP file with the person’s notes, the project’s details and versions, and what they
        chose to include: the app log, a picture of the Teslyn Field window, the project’s datasets, the
        exceedance workbook, the map content and raw basemag files. Passwords, keys, sign-in codes and email
        addresses are taken out before it leaves the laptop. Packages are removed for good{' '}
        {DIAGNOSTICS_KEEP_DAYS} days after they were sent; download one to keep it longer.
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--warn'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="diagnostics" />}
      {rows && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Sent</th>
                <th className="label">From</th>
                <th className="label">Project</th>
                <th className="label">Notes</th>
                <th className="label">What is in it</th>
                <th className="label">Size</th>
                <th className="label">Kept</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    No diagnostics yet. In Teslyn Field, Help, Send diagnostics sends them here.
                  </td>
                </tr>
              )}
              {rows.map((p) => (
                <tr key={p.id}>
                  <td className="caption">
                    {time(p.sent_at ?? p.created_at)}
                    <div className="caption muted data">{p.file_name}</div>
                  </td>
                  <td>
                    {p.sent_by ? (names.get(p.sent_by) ?? 'A staff member') : 'Someone no longer in Teslyn'}
                    <div className="caption muted">
                      Teslyn Field {p.app_version} ({p.channel})
                    </div>
                  </td>
                  <td className="data">{p.project_number ?? 'None open'}</td>
                  <td className="body">{p.notes || <span className="muted">No notes</span>}</td>
                  <td className="caption">
                    {diagnosticsContentsText(p.contents.map((c) => c.name))}
                    {p.left_out.length > 0 && (
                      <div className="muted">
                        Left out: {p.left_out.map((l) => `${l.name} (${l.reason})`).join('; ')}
                      </div>
                    )}
                  </td>
                  <td className="data">{exportSizeText(p.size_bytes)}</td>
                  <td className="caption">
                    {p.status === 'sent'
                      ? diagnosticsKeptText(p.sent_at ?? p.created_at, now)
                      : 'Not finished: the upload stopped. It is removed within a day.'}
                  </td>
                  <td className="table__actions">
                    {confirming === p.id ? (
                      <>
                        <Button variant="primary" busy={deleting === p.id} onClick={() => void remove(p)}>
                          Delete for good
                        </Button>
                        <Button variant="quiet" onClick={() => setConfirming(null)}>
                          Keep
                        </Button>
                      </>
                    ) : (
                      <>
                        {p.status === 'sent' && (
                          <Button
                            variant="quiet"
                            aria-label={`Download ${p.file_name}`}
                            onClick={() => void download(p)}
                          >
                            Download
                          </Button>
                        )}
                        <Button
                          variant="quiet"
                          aria-label={`Delete ${p.file_name}`}
                          onClick={() => setConfirming(p.id)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
